// Epitaph modal — blocking overlay shown when a bot corporation is eliminated.
//
// Triggered by the elimination-detection logic in dashboard/cycle-report after
// a `player_eliminated` WS event (or, for v0.1, after the tick-report marks a
// bot player as bankrupt/indicted). The modal pulls:
//   - Headline:  t('epitaph.eliminated_headline', { corp: name })
//   - Body:      t('botcorp.<key>.epitaph')
//   - Close:     t('epitaph.close')
//   - ARIA lbl:  t('epitaph.dialog_label')
//
// UX invariants (per SPEC_bot_personalities.md §8):
//   - Full-screen overlay, role="dialog" + aria-modal="true"
//   - Focus-trap: OK button is focused on open, Tab is clamped to it
//   - ESC / click-outside / OK-button all close the modal
//   - Multiple eliminations in the same tick are queued and shown sequentially
//     — never two overlays stacked at the same time.
//
// Human players have no `corpKey` and never trigger this — the bankruptcy
// path for humans falls back to the existing Gazette + leaderboard flow.

import { t } from '../i18n';

interface EpitaphRequest {
  corpKey: string;
  corpName: string;
}

let activeOverlay: HTMLElement | null = null;
let queue: EpitaphRequest[] = [];

/**
 * Enqueue an epitaph modal. If one is already visible, the request is queued
 * and shown after the current modal closes. Calls without a `corpKey` (human
 * players, pre-v1.1 fixtures) are silently dropped — this keeps the trigger
 * site simple: it always calls through, this module decides whether to show.
 */
export function showEpitaph(req: { corpKey?: string; corpName: string }): void {
  if (!req.corpKey) return;
  queue.push({ corpKey: req.corpKey, corpName: req.corpName });
  if (!activeOverlay) flushQueue();
}

/** Test-only: discard any pending epitaphs. */
export function _resetEpitaphQueue(): void {
  queue = [];
  if (activeOverlay) {
    activeOverlay.remove();
    activeOverlay = null;
  }
}

function flushQueue(): void {
  const next = queue.shift();
  if (!next) return;
  renderOverlay(next);
}

function renderOverlay(req: EpitaphRequest): void {
  const bodyKey = `botcorp.${req.corpKey}.epitaph`;
  const body = t(bodyKey);
  // If i18n has no epitaph for this corp, skip silently and flush the next —
  // better than showing a raw key as the obituary text.
  if (!body || body === bodyKey) {
    flushQueue();
    return;
  }
  const headline = t('epitaph.eliminated_headline', { corp: req.corpName });
  const closeLabel = t('epitaph.close');
  const dialogLabel = t('epitaph.dialog_label');

  const overlay = document.createElement('div');
  overlay.id = 'epitaph-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', dialogLabel);
  overlay.style.cssText = `
    position:fixed; inset:0; z-index:10000;
    background:rgba(4,6,10,0.88);
    display:flex; align-items:center; justify-content:center;
    font-family:var(--mono);
    backdrop-filter:blur(4px);
  `;

  const panel = document.createElement('div');
  panel.style.cssText = `
    background:var(--surface-modal); backdrop-filter:blur(4px); -webkit-backdrop-filter:blur(4px);
    border:1px solid rgba(231,76,60,0.55);
    box-shadow:0 0 48px rgba(231,76,60,0.25), 0 8px 32px rgba(0,0,0,0.6);
    max-width:520px; width:90%;
    padding:28px 32px;
    color:var(--text-hi);
    text-align:center;
  `;

  const head = document.createElement('div');
  head.textContent = headline;
  head.style.cssText = `
    color:var(--red);
    font-size:0.85rem;
    letter-spacing:3px;
    text-transform:uppercase;
    margin-bottom:20px;
  `;

  const bodyEl = document.createElement('div');
  bodyEl.textContent = body;
  bodyEl.style.cssText = `
    font-size:1.05rem;
    line-height:1.55;
    color:var(--text-hi);
    font-style:italic;
    margin-bottom:28px;
  `;

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = closeLabel;
  btn.className = 'epitaph-ok';
  btn.style.cssText = `
    background:transparent;
    color:var(--cyan);
    border:1px solid var(--cyan);
    padding:8px 28px;
    font-family:inherit;
    font-size:0.9rem;
    letter-spacing:2px;
    cursor:pointer;
    text-transform:uppercase;
  `;

  panel.appendChild(head);
  panel.appendChild(bodyEl);
  panel.appendChild(btn);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
  activeOverlay = overlay;

  const close = (): void => {
    if (activeOverlay !== overlay) return;
    document.removeEventListener('keydown', onKey, true);
    overlay.remove();
    activeOverlay = null;
    // Next elimination in the queue, if any.
    if (queue.length > 0) flushQueue();
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === 'Tab') {
      // Focus-trap: only one focusable element in this modal, keep it focused.
      e.preventDefault();
      btn.focus();
    }
  };

  btn.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    // Click on the dimmed background (not the inner panel) → close.
    if (e.target === overlay) close();
  });
  document.addEventListener('keydown', onKey, true);

  // Move focus into the modal so keyboard users and screenreaders land here.
  btn.focus();
}
